import React, { useCallback, useMemo, useRef } from "react";
import { PageLayout } from "@Local/Components/Layout";
import { Grid } from "@mui/material";
import { AutocompleteField, Box, Button, Chip, DateIntervalField, IconButton, Icons, MenuItem, SelectField, Stack, TextField, Typography } from "@curves/ui-base";
import { Overview } from "@curves/ui-shared";
import useOverview from "@Util/hook/useOverview";
import useInvoiceTriplicateUniformApi from "@Apis/useInvoiceTriplicateUniformApi";
import useOptions from "@Util/hook/useOptions";
import { Link } from "react-router-dom";
import { ORDERS_VIEW_ROUTER_URL } from "@Router/routerPath";
import { ORDER_STATUS_COLOR_MATCH } from "@Constants/appConfig";
import { ALLOWANCE_STATUS } from "@Constants/";
import { formatCurrencyFn, isEmpty, isFunction, refIsRequiredError } from "@Util/utils";
import { filterMenu } from "@Local/Orders/Orders.util";
import { getTriplicateAllowanceMenuMap } from "./TriplicateAllowance.util";
import { getCurrentEndDay, getCurrentStartDate, getFirstOfMonth, getToday } from "@Util/moment.util";
import { OverflowMenu } from "@Local/Components";
import DateTimeDisplay from "../Components/RowLabel/DateTimeDisplay";
import FinanceDialogBox from "@Local/Finance/Components/FinanceDialogBox";

const triplicateAllowanceOverviewHeaderRow = [
	{
		cellKey: "orderNo",
		headerLabel: "訂單",
		align: "center",
		width: "18",
		formatCell: (orderNo, row) => {
			const { orderID, invoiceProcessStatusText, triplicateAllowanceStatusText, invoiceProcessStatusColor, triplicateAllowanceStatusColor, statusText, statusColor } = row;
			const chipLabel = invoiceProcessStatusText ?? triplicateAllowanceStatusText ?? statusText ?? "—";
			const colorKey = invoiceProcessStatusColor ?? triplicateAllowanceStatusColor ?? statusColor ?? 3;
			return (
				<Stack direction={"column"}>
					<Link className={"mb-1"} to={ORDERS_VIEW_ROUTER_URL(orderID)}>
						{orderNo}
					</Link>
					<Chip label={chipLabel} color={ORDER_STATUS_COLOR_MATCH[colorKey] ?? "gray"} title={"折讓狀態"} />
				</Stack>
			);
		},
	},
	{
		cellKey: "receiverName",
		headerLabel: "購買人",
		width: "14",
		formatCell: (receiverName, { receiverTypeText }) => {
			return (
				<React.Fragment>
					<Typography variant={"subtitle1"} className={"p-0 font-weight-bold"}>
						{receiverName ?? "—"}
					</Typography>
					<Typography variant={"body2"} className={"mb-0 font-color-3"}>{`身份：${receiverTypeText ?? "—"}`}</Typography>
				</React.Fragment>
			);
		},
	},
	{
		cellKey: "clubName",
		headerLabel: "銷售店鋪",
		width: "12",
		align: "center",
	},
	{
		cellKey: "triplicateInvoice",
		headerLabel: "三聯發票內容",
		width: "18",
		formatCell: (_, { taxID, taxTitle }) => (
			<Stack direction="column" alignItems="flex-start">
				<Typography variant="body2" className="mb-0">{`統一編號：${taxID ?? "—"}`}</Typography>
				<Typography variant="body2" className="mb-0">{`發票抬頭：${taxTitle ?? "—"}`}</Typography>
			</Stack>
		),
	},
	{
		cellKey: "payablePrice",
		headerLabel: "訂單金額",
		align: "right",
		width: "12",
		formatCell: cellValue => formatCurrencyFn(cellValue, { symbol: "$" }),
	},
	{
		cellKey: "orderDate",
		headerLabel: "建立日期",
		width: "14",
		formatCell: orderDate => <DateTimeDisplay date={orderDate} />,
	},
	{
		cellKey: "more",
		headerLabel: "",
		width: 6,
		// openReviewCertificate 來自 Overview.Table 的 cellRenderContext（核准成功後由 FinanceDialogBox 呼叫 refreshList 重打列表）
		formatCell: (_, rowData, { openReviewCertificate }) => {
			const menuData = filterMenu(getTriplicateAllowanceMenuMap(rowData, { openReviewCertificate }));
			return <OverflowMenu menuData={menuData} enabled={!isEmpty(menuData)} />;
		},
	},
];

/**
 * 財務作業 — 三聯發票折讓總覽（HQ）
 * getParamsData：組 filters；useOverview 內呼叫 postInvoiceTriplicateUniformOverviewApi
 */
const TriplicateInvoiceAllowanceOverviewPage = () => {
	const {
		getInvoiceTriplicateUniformOverviewOptionsApi,
		postInvoiceTriplicateUniformOverviewApi,
		deleteInvoiceTriplicateUniformOverviewCacheApi,
	} = useInvoiceTriplicateUniformApi();

	const { options } = useOptions({
		getOptionsApi: getInvoiceTriplicateUniformOverviewOptionsApi,
		isFetchOnMount: true,
	});

	const { allowanceStatusOptions = [], clubOptions = [] } = options || {};

	const defaultSearchParams = useMemo(
		() => {
			const today = getToday();
			return {
				pageIndex: 1,
				pageSize: 20,
				name: "",
				startDate: getCurrentStartDate(getFirstOfMonth(today, -3)),
				endDate: getCurrentEndDay(today),
				allowanceStatus: ALLOWANCE_STATUS.ALL,
				clubID: 0, // 銷售店鋪：預設全部
			};
		},
		// eslint-disable-next-line
		[],
	);

	const { searchParams, overviewData, getOverviewData, updateOverviewPage } = useOverview({
		getOverviewApi: postInvoiceTriplicateUniformOverviewApi,
		defaultSearchParams,
		enableQueryString: true,
		enableInfiniteScroll: false,
		isFetchOnMount: false,
	});

	const nameRef = useRef(null);
	const dateRangeRef = useRef(null);
	const allowanceStatusRef = useRef(null);
	const clubRef = useRef(null);

	const pageIndex = searchParams?.pageIndex;
	const pageSize = searchParams?.pageSize;

	// 組出給 useOverview 的 filters（POST body：TriplicateAllowancePaginationRequestModel；以表單 ref 為準）
	const getParamsData = useCallback(() => {
		const nameVal = nameRef.current?.getResult?.();
		const dateRange = dateRangeRef.current?.getResult?.();
		const statusVal = allowanceStatusRef.current?.getResult?.();
		const clubID = clubRef.current?.getResult?.("value") ?? 0;

		let startDate = "";
		let endDate = "";
		if (dateRange) {
			const { startDate: start, endDate: end } = dateRange;
			startDate = getCurrentStartDate(start);
			endDate = getCurrentEndDay(end);
		}

		return {
			pageIndex: pageIndex || 1,
			pageSize: pageSize || 20,
			name: nameVal != null ? String(nameVal).trim() : "",
			startDate,
			endDate,
			allowanceStatus: statusVal ?? ALLOWANCE_STATUS.ALL,
			clubID: Number(clubID) || 0,
		};
	}, [pageIndex, pageSize]);

	const handleSearchSubmit = useCallback(() => {
		if (refIsRequiredError(allowanceStatusRef, nameRef, dateRangeRef, clubRef)) return false;
		return getParamsData();
	}, [getParamsData]);

	// 核准／清暫存後重打列表（clearCache 時一併 DELETE overview-cache；silent 略過 refresh 內 API 的全域 loading）
	const refreshList = useCallback(
		async ({ clearCache = false, clubID: clubIDOverride, silent = false } = {}) => {
			const loadingOpt = silent ? { isLoadingVisible: false } : {};
			if (clearCache) {
				const clubID = Number(clubIDOverride ?? searchParams?.clubID) || 0;
				await deleteInvoiceTriplicateUniformOverviewCacheApi({ clubID }, loadingOpt);
			}
			return getOverviewData(searchParams);
		},
		[deleteInvoiceTriplicateUniformOverviewCacheApi, getOverviewData, searchParams],
	);

	const handleOverviewCacheRefresh = useCallback(async () => {
		const clubID = Number(searchParams?.clubID) || 0;
		const resp = await deleteInvoiceTriplicateUniformOverviewCacheApi({ clubID });
		if (resp?.success !== false) {
			await getOverviewData(searchParams);
		}
	}, [deleteInvoiceTriplicateUniformOverviewCacheApi, getOverviewData, searchParams]);

	// FinanceDialogBox imperative API（折讓證明審核等彈窗）
	const dialogRef = useRef(null);

	// 開啟折讓證明審核；封裝 confirmPath 檢查與 dialogRef 呼叫，供 getTriplicateAllowanceMenuMap 使用
	const openReviewCertificate = useCallback(
		rowData => {
			if (!rowData?.confirmPath) return;
			const openDialog = dialogRef.current?.openReviewTriplicateAllowanceCertificateDialog;
			if (!isFunction(openDialog)) return;
			openDialog(rowData, () => refreshList({ clearCache: true, clubID: rowData.clubID, silent: true }));
		},
		[refreshList],
	);

	const overviewContextValue = useMemo(
		() => ({
			overviewData,
			searchParams,
			getOverviewData,
			updateOverviewPage,
		}),
		[overviewData, searchParams, getOverviewData, updateOverviewPage],
	);

	// 傳入 Overview.Table formatCell 的共用 context
	const cellRenderContext = useMemo(() => ({ openReviewCertificate }), [openReviewCertificate]);

	return (
		<PageLayout>
			<PageLayout.Header title="三聯發票折讓總覽" />
			<Box className="px-0">
				<Overview contextValue={overviewContextValue} idKey="orderReturnID" className="triplicate-invoice-allowance-overview">
					<Overview.Search className="p-3 mb-3" getSearchParamsOnSubmit={handleSearchSubmit}>
						<Grid spacing={2} container>
							<Grid xs={8} item>
								<TextField
									label={"會員/編號"}
									ref={nameRef}
									key={searchParams?.name}
									defaultValue={searchParams.name}
									InputProps={{ placeholder: "請輸入訂單編號或購買人或會員編號" }}
									fullWidth
								/>
							</Grid>
							<Grid xs={4} item>
								<SelectField label={"折讓狀態"} ref={allowanceStatusRef} key={searchParams?.allowanceStatus} defaultValue={searchParams?.allowanceStatus} fullWidth required>
									{allowanceStatusOptions.map(({ text, value, disabled }) => (
										<MenuItem key={value} value={value} disabled={disabled}>
											{text}
										</MenuItem>
									))}
								</SelectField>
							</Grid>
							<Grid xs={8} item>
								<DateIntervalField
									label={"建立日期"}
									ref={dateRangeRef}
									startDateProps={{
										key: searchParams?.startDate,
										defaultValue: searchParams?.startDate,
									}}
									endDateProps={{
										key: searchParams?.endDate,
										defaultValue: searchParams?.endDate,
									}}
									fullWidth
								/>
							</Grid>
							<Grid xs={4} item>
								<AutocompleteField
									key={`${searchParams?.clubID}_${clubOptions.length}`}
									label={"銷售店鋪"}
									inputProps={{ placeholder: "請輸入店名/店碼" }}
									ref={clubRef}
									options={clubOptions}
									optionKeys={["text"]}
									primaryKey={"value"}
									defaultValue={searchParams?.clubID}
									required
									fullWidth
								/>
							</Grid>
							<Grid xs={12} item>
								<Stack direction="row" justifyContent="end" alignItems="center">
									<Button variant="contained" color="primary" type="submit">
										搜尋
									</Button>
								</Stack>
							</Grid>
						</Grid>
					</Overview.Search>

					<Stack direction="row" justifyContent="space-between" alignItems="center" className="mb-2">
						<Box />
						<Stack direction="row" alignItems="center">
							<Overview.TotalCount className="mb-0" showCurrentCount />
							<IconButton onClick={handleOverviewCacheRefresh} aria-label="重新整理">
								<Icons.Refresh />
							</IconButton>
						</Stack>
					</Stack>

					<Overview.Table className="mb-3" headerRow={triplicateAllowanceOverviewHeaderRow} cardProps={{ className: "theme-gray overview" }} cellRenderContext={cellRenderContext} />
					<Overview.Pagination />
				</Overview>
			</Box>
			<FinanceDialogBox ref={dialogRef} />
		</PageLayout>
	);
};

export default TriplicateInvoiceAllowanceOverviewPage;
